import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescriptionHidden,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/custom/searchable-select'
import { useCompaniesDropdownSearch } from '@/hooks/use-reference-data'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { type AdminUser, useCreateCompanyAssignment } from '@/hooks/use-admin'
import { showSuccess, showError } from '@/lib/toast'

const ASSIGNMENT_TYPES = [
  { value: 'PRIMARY_COVERAGE', label: 'Primary Owner' },
  { value: 'SECONDARY_COVERAGE', label: 'Secondary Owner' },
]

interface AssignCompanyModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: AdminUser
}

export function AssignCompanyModal({
  open,
  onOpenChange,
  user,
}: AssignCompanyModalProps) {
  const isExternal = user.userType === 'EXTERNAL'
  const [companyId, setCompanyId] = useState('')
  const [assignmentType, setAssignmentType] = useState('PRIMARY_COVERAGE')
  const [companySearch, setCompanySearch] = useState('')
  const debouncedSearch = useDebouncedValue(companySearch, 300)

  const { data: companiesData, isLoading: companiesLoading } =
    useCompaniesDropdownSearch({
      search: debouncedSearch,
      enabled: open,
    })

  const createAssignment = useCreateCompanyAssignment()

  const existingCompanyIds = new Set(
    user.companyAssignments.map((a) => a.companyId)
  )

  const availableCompanies =
    companiesData?.data?.filter((c) => !existingCompanyIds.has(c.id)) ?? []

  const handleSubmit = async () => {
    if (!companyId) return

    const selectedCompany = availableCompanies.find((c) => c.id === companyId)

    try {
      await createAssignment.mutateAsync({
        userId: user.id,
        companyId,
        companyName: selectedCompany?.displayName,
        assignmentType,
      })
      showSuccess('Company assigned')
      onOpenChange(false)
      setCompanyId('')
      setAssignmentType('PRIMARY_COVERAGE')
    } catch (error) {
      showError(error, 'Failed to create assignment')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xs p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-0">
          <DialogTitle className="text-[15px]">Assign company</DialogTitle>
          <DialogDescriptionHidden>
            Assign {user.name} to a company
          </DialogDescriptionHidden>
        </DialogHeader>

        {/* User context — just name and email, inline */}
        <div className="px-5 pt-1 pb-4">
          <Typography variant="body-sm" className="text-muted-foreground">
            {user.name} &middot; {user.email}
          </Typography>
        </div>

        <div className="border-t border-primary/5 px-5 py-4 flex flex-col gap-3">
          {/* Company select */}
          <div>
            <Typography
              variant="body-sm"
              as="span"
              className="mb-1 block text-[11px] font-medium text-primary/70 uppercase tracking-wide"
            >
              Company
            </Typography>
            <SearchableSelect
              options={availableCompanies.map((c) => ({
                value: c.id,
                label: c.displayName,
              }))}
              value={companyId}
              onValueChange={(v) => setCompanyId(v ?? '')}
              onSearchChange={setCompanySearch}
              placeholder="Search companies..."
              searchPlaceholder="Search companies..."
              isLoading={companiesLoading}
              showSearchIcon
            />
          </div>

          {/* Coverage type — only for internal users */}
          {!isExternal && (
            <div>
              <Typography
                variant="body-sm"
                as="span"
                className="mb-1 block text-[11px] font-medium text-primary/70 uppercase tracking-wide"
              >
                Ownership
              </Typography>
              <Select value={assignmentType} onValueChange={setAssignmentType}>
                <SelectTrigger className="border-primary/20 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASSIGNMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="border-t border-primary/5 px-5 py-3 flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-xs"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="text-xs"
            onClick={handleSubmit}
            disabled={!companyId || createAssignment.isPending}
          >
            {createAssignment.isPending ? 'Assigning...' : 'Assign'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
